// SF2 — Conversion Rate Analysis, the trend half.
//
// Both rates are plotted together because Amazon publishes both and they
// diverge on multi-unit orders: one order of six units is a single order item
// and six units, so unit conversion runs above order conversion wherever
// baskets are deep. The gap between the lines is basket depth.
//
// The line is account-wide. Per-product conversion lives in the table below and
// cannot be trended, because Amazon publishes per-ASIN traffic only as one
// total for the whole report period.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../ProductPerformance.module.css';
import { ClippedWindow } from '../../Sales/components/format';
import { getConversionTrend } from '../services/productPerformanceApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const count = (value) =>
  value === null || value === undefined ? '—' : inr.format(value);

const percent = (ratio, digits = 2) =>
  ratio === null || ratio === undefined ? '—' : `${(ratio * 100).toFixed(digits)}%`;

// Recharts plots numbers, so the ratios are scaled to percent for the axis and
// carried back through the tooltip in the same unit.
const SERIES = [
  { key: 'unit_pct', name: 'Units', colour: '#1f6feb' },
  { key: 'order_pct', name: 'Orders', colour: '#8250df' },
];

const PointDetail = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className={local.tooltip}>
      <div className={local.tooltipHead}>{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className={local.tooltipRow}>
          <span className={local.tooltipSwatch} style={{ background: entry.color }} />
          <span>{entry.name}</span>
          <strong>{entry.value === null ? '—' : `${entry.value.toFixed(2)}%`}</strong>
        </div>
      ))}
      {/* The raw counts, so the rate is auditable rather than trusted. */}
      <div className={local.tooltipFoot}>
        {count(row.sessions)} sessions · {count(row.units_ordered)} units ·{' '}
        {count(row.order_items)} order items
      </div>
    </div>
  );
};

const ConversionTrend = ({ window: reportingWindow, onLoadingChange, onData }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getConversionTrend(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Shared with the page's KPI strip, so the same figures are not fetched twice.
  useEffect(() => { onData?.(data); }, [data, onData]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const points = (data?.points || []).map((point) => ({
    ...point,
    unit_pct: point.unit_conversion === null ? null : point.unit_conversion * 100,
    order_pct: point.order_conversion === null ? null : point.order_conversion * 100,
  }));

  const totals = data?.totals;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Conversion</h2>
        {data && data.days_with_data < data.days_in_window && (
          <span className={local.muted}>
            {data.days_with_data} of {data.days_in_window} days have data
          </span>
        )}
      </div>
      <p className={local.muted}>
        The share of visits that became a purchase, across the catalogue. Unit
        conversion sits above order conversion wherever buyers take more than
        one of something.
      </p>
      <ClippedWindow data={data} />

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load conversion'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <>
          <div className={local.ratios}>
            <div>
              <div className={local.ratioLabel}>Unit conversion</div>
              <div className={local.ratioValue}>{percent(totals?.unit_conversion)}</div>
            </div>
            <div>
              <div className={local.ratioLabel}>Order conversion</div>
              <div className={local.ratioValue}>{percent(totals?.order_conversion)}</div>
            </div>
            <div>
              <div className={local.ratioLabel}>Sessions</div>
              <div className={local.ratioValue}>{count(totals?.sessions)}</div>
            </div>
            <div>
              <div className={local.ratioLabel}>Order items</div>
              <div className={local.ratioValue}>{count(totals?.order_items)}</div>
            </div>
          </div>

          <div className={local.plot}>
            {points.length === 0 && !loading ? (
              <div className={local.emptyCell}>No traffic in this window.</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={points} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                  <CartesianGrid stroke="#eef0f3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={false} axisLine={{ stroke: '#e3e6ea' }} minTickGap={28} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false}
                    axisLine={false} width={54}
                    tickFormatter={(value) => `${value.toFixed(1)}%`} />
                  <Tooltip content={<PointDetail />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {SERIES.map((series) => (
                    <Line key={series.key} type="monotone" dataKey={series.key}
                      name={series.name} stroke={series.colour} strokeWidth={2}
                      dot={false} connectNulls={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ConversionTrend;
